/**
 * Cancel intent pieces of the B2C picker (PLA-878): the quiet
 * "Termin stornieren" link under the grid, and the one-step cancel confirmation.
 */
import { FC } from "react"
import { ActivityIndicator, Pressable, TextStyle, View, ViewStyle } from "react-native"
import Icon from "react-native-vector-icons/MaterialIcons"

import { Button } from "@/components/Button"
import { Text } from "@/components/Text"
import { colors, spacing } from "@/theme"

export const CancelLink: FC<{ onPress: () => void }> = ({ onPress }) => (
  <View style={$linkWrap}>
    <Pressable accessibilityRole="button" onPress={onPress} hitSlop={8}>
      <Text style={$link}>Termin stornieren</Text>
    </Pressable>
  </View>
)

export interface CancelConfirmViewProps {
  /** "<Wochentag>, <D>. <Monat> · HH:MM Uhr" or null when unknown. */
  appointment: string | null
  isLoading: boolean
  errorMessage: string | null
  onBack: () => void
  onConfirm: () => void
}

export const CancelConfirmView: FC<CancelConfirmViewProps> = ({
  appointment,
  isLoading,
  errorMessage,
  onBack,
  onConfirm,
}) => (
  <View>
    <Text style={$heading}>Termin stornieren?</Text>
    {appointment && <Text style={$appointment}>{appointment}</Text>}
    {errorMessage && (
      <View style={$error}>
        <Icon name="error-outline" size={20} color={colors.error} />
        <Text style={$errorText}>{errorMessage}</Text>
      </View>
    )}
    <View style={$buttons}>
      <Button text="Zurück" preset="default" onPress={onBack} style={$flex} disabled={isLoading} />
      <Button
        text={isLoading ? "Wird storniert..." : "Stornieren"}
        preset="filled"
        onPress={onConfirm}
        disabled={isLoading}
        style={[$flex, $destructive]}
        textStyle={$destructiveText}
        RightAccessory={
          isLoading ? () => <ActivityIndicator size="small" color={colors.surface} /> : undefined
        }
      />
    </View>
  </View>
)

const $flex: ViewStyle = { flex: 1 }
const $linkWrap: ViewStyle = {
  alignItems: "center",
  marginTop: spacing.lg,
  paddingTop: spacing.md,
  borderTopWidth: 1,
  borderTopColor: colors.border,
}
const $link: TextStyle = {
  fontSize: 13,
  color: colors.textDim,
  textDecorationLine: "underline",
}
const $heading: TextStyle = { fontSize: 20, fontWeight: "800", color: colors.text }
const $appointment: TextStyle = {
  fontSize: 16,
  fontWeight: "600",
  color: colors.text,
  marginTop: spacing.sm,
}
const $error: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  backgroundColor: colors.errorBgLight,
  padding: spacing.sm,
  borderRadius: 8,
  marginTop: spacing.md,
}
const $errorText: TextStyle = { fontSize: 14, color: colors.error, marginLeft: spacing.xs, flex: 1 }
const $buttons: ViewStyle = { flexDirection: "row", gap: spacing.sm, marginTop: spacing.lg }
const $destructive: ViewStyle = { backgroundColor: colors.primary }
const $destructiveText: TextStyle = { color: colors.surface }
